import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  ChevronDown,
  Clock,
  ArrowRight,
  CheckCircle,
  Zap,
  Target,
  BookOpen,
  Award
} from 'lucide-react'

const phases = [
  {
    id: 1,
    phase: 'Phase 1',
    title: 'Linux & Networking Fundamentals',
    duration: '3–4 Weeks',
    difficulty: 'Beginner',
    diffColor: 'bg-green-100 text-green-700',
    accent: 'from-emerald-500 to-teal-600',
    lightAccent: 'bg-emerald-50 border-emerald-200',
    iconBg: 'bg-emerald-100 text-emerald-700',
    description: 'Build a rock-solid foundation with Linux OS, shell scripting, and core networking concepts — the backbone of every DevOps workflow.',
    tools: ['Ubuntu / CentOS', 'Bash', 'SSH', 'TCP/IP', 'DNS', 'Nginx', 'Vim'],
    skills: [
      'File system navigation & permissions',
      'Shell scripting & automation',
      'Process & service management',
      'Networking (IP, ports, firewalls)',
      'Package management (apt/yum)',
    ],
    outcome: 'Confidently operate any Linux server from the command line',
  },
  {
    id: 2,
    phase: 'Phase 2',
    title: 'Version Control with Git',
    duration: '1–2 Weeks',
    difficulty: 'Beginner',
    diffColor: 'bg-green-100 text-green-700',
    accent: 'from-sky-500 to-blue-600',
    lightAccent: 'bg-sky-50 border-sky-200',
    iconBg: 'bg-sky-100 text-sky-700',
    description: 'Master Git workflows used in professional teams. Learn branching strategies, pull requests, and collaborative development practices.',
    tools: ['Git', 'GitHub', 'GitLab', 'Bitbucket', 'Git Flow'],
    skills: [
      'Branching, merging & rebasing',
      'Pull requests & code reviews',
      'Git hooks & automation',
      'Monorepo strategies',
      'Conflict resolution',
    ],
    outcome: 'Manage codebases using industry-standard Git workflows',
  },
  {
    id: 3,
    phase: 'Phase 3',
    title: 'Containerization with Docker',
    duration: '3 Weeks',
    difficulty: 'Intermediate',
    diffColor: 'bg-blue-100 text-blue-700',
    accent: 'from-blue-500 to-indigo-600',
    lightAccent: 'bg-blue-50 border-blue-200',
    iconBg: 'bg-blue-100 text-blue-700',
    description: 'Package applications into portable containers. Understand image layering, multi-stage builds, and orchestrate multi-container apps with Docker Compose.',
    tools: ['Docker', 'Docker Compose', 'Docker Hub', 'Alpine Linux', 'Buildkit'],
    skills: [
      'Writing production-grade Dockerfiles',
      'Multi-stage builds & image optimization',
      'Docker networking & volumes',
      'Docker Compose for local dev',
      'Container security best practices',
    ],
    outcome: 'Build, ship, and run containerized applications at scale',
  },
  {
    id: 4,
    phase: 'Phase 4',
    title: 'Kubernetes & Container Orchestration',
    duration: '4–5 Weeks',
    difficulty: 'Intermediate',
    diffColor: 'bg-blue-100 text-blue-700',
    accent: 'from-violet-500 to-purple-600',
    lightAccent: 'bg-violet-50 border-violet-200',
    iconBg: 'bg-violet-100 text-violet-700',
    description: 'Orchestrate containers at scale with Kubernetes. Covers deployments, services, ingress, RBAC, Helm charts, and CKA exam prep.',
    tools: ['Kubernetes', 'kubectl', 'Helm', 'Minikube', 'k3s', 'Lens', 'kubeadm'],
    skills: [
      'Pods, Deployments & ReplicaSets',
      'Services, Ingress & Load Balancing',
      'ConfigMaps, Secrets & RBAC',
      'Persistent Volumes & StatefulSets',
      'Helm chart development',
    ],
    outcome: 'Pass the CKA exam and deploy production Kubernetes clusters',
    badge: 'CKA Certified',
  },
  {
    id: 5,
    phase: 'Phase 5',
    title: 'CI/CD Pipelines',
    duration: '3 Weeks',
    difficulty: 'Intermediate',
    diffColor: 'bg-blue-100 text-blue-700',
    accent: 'from-orange-500 to-red-500',
    lightAccent: 'bg-orange-50 border-orange-200',
    iconBg: 'bg-orange-100 text-orange-700',
    description: 'Automate the entire software delivery lifecycle. Build pipelines that test, build, and deploy code to any environment automatically.',
    tools: ['Jenkins', 'GitHub Actions', 'GitLab CI', 'ArgoCD', 'SonarQube', 'Nexus'],
    skills: [
      'Declarative pipeline as code',
      'Automated testing integration',
      'Docker image build & push',
      'GitOps with ArgoCD',
      'Environment promotion strategies',
    ],
    outcome: 'Build end-to-end CI/CD pipelines for real production systems',
  },
  {
    id: 6,
    phase: 'Phase 6',
    title: 'Infrastructure as Code',
    duration: '3 Weeks',
    difficulty: 'Intermediate',
    diffColor: 'bg-blue-100 text-blue-700',
    accent: 'from-amber-500 to-yellow-500',
    lightAccent: 'bg-amber-50 border-amber-200',
    iconBg: 'bg-amber-100 text-amber-700',
    description: 'Provision and manage cloud infrastructure programmatically. Eliminate manual click-ops and version-control your entire infrastructure.',
    tools: ['Terraform', 'Ansible', 'Packer', 'Vault', 'Terragrunt'],
    skills: [
      'Terraform state management & modules',
      'Ansible playbooks & roles',
      'Immutable infrastructure patterns',
      'Secrets management with Vault',
      'Cloud resource provisioning',
    ],
    outcome: 'Provision production cloud infra with zero manual intervention',
  },
  {
    id: 7,
    phase: 'Phase 7',
    title: 'Cloud Platforms (AWS / Azure)',
    duration: '4 Weeks',
    difficulty: 'Advanced',
    diffColor: 'bg-red-100 text-red-700',
    accent: 'from-yellow-500 to-orange-500',
    lightAccent: 'bg-yellow-50 border-yellow-200',
    iconBg: 'bg-yellow-100 text-yellow-700',
    description: 'Master cloud services on AWS and Azure. Design scalable, highly-available, cost-optimized architectures and earn cloud certifications.',
    tools: ['AWS EC2/EKS/S3/RDS/Lambda', 'Azure VMs/AKS', 'CloudFormation', 'IAM', 'VPC'],
    skills: [
      'VPC design & security groups',
      'Managed Kubernetes (EKS/AKS)',
      'Serverless with Lambda/Functions',
      'Cost optimization strategies',
      'High-availability architecture',
    ],
    outcome: 'Architect and deploy production workloads on AWS & Azure',
    badge: 'AWS Certified',
  },
  {
    id: 8,
    phase: 'Phase 8',
    title: 'Monitoring & Observability',
    duration: '2–3 Weeks',
    difficulty: 'Advanced',
    diffColor: 'bg-red-100 text-red-700',
    accent: 'from-pink-500 to-rose-600',
    lightAccent: 'bg-pink-50 border-pink-200',
    iconBg: 'bg-pink-100 text-pink-700',
    description: 'Build full observability into your systems. Set up metrics, logs, traces, and alerting to detect and resolve incidents before users notice.',
    tools: ['Prometheus', 'Grafana', 'ELK Stack', 'Jaeger', 'PagerDuty', 'Datadog'],
    skills: [
      'Prometheus metrics & PromQL',
      'Grafana dashboard design',
      'Centralized logging with ELK',
      'Distributed tracing',
      'SLO/SLA alerting strategies',
    ],
    outcome: 'Build complete observability stacks for production systems',
  },
  {
    id: 9,
    phase: 'Phase 9',
    title: 'DevSecOps & Production Deployment',
    duration: '2 Weeks',
    difficulty: 'Advanced',
    diffColor: 'bg-red-100 text-red-700',
    accent: 'from-primary-500 to-primary-700',
    lightAccent: 'bg-primary-50 border-primary-200',
    iconBg: 'bg-primary-100 text-primary-700',
    description: 'Embed security into every stage of the pipeline. Master production deployment strategies and run enterprise-grade DevOps workflows end-to-end.',
    tools: ['Trivy', 'Snyk', 'OWASP ZAP', 'Blue/Green', 'Canary Releases', 'Chaos Engineering'],
    skills: [
      'Container & dependency scanning',
      'SAST/DAST integration in CI',
      'Zero-downtime deployment strategies',
      'Disaster recovery planning',
      'On-call & incident response',
    ],
    outcome: 'Lead production deployments and security-first DevOps culture',
  },
]

const totalWeeks = '24–30'

const overviewStats = [
  { value: '9', label: 'Learning Phases', icon: <Target className="w-5 h-5" /> },
  { value: totalWeeks, label: 'Total Weeks', icon: <Clock className="w-5 h-5" /> },
  { value: '30+', label: 'Tools Covered', icon: <Zap className="w-5 h-5" /> },
  { value: '3', label: 'Certifications', icon: <Award className="w-5 h-5" /> },
]

const DevOpsRoadmap = () => {
  const [openPhase, setOpenPhase] = useState(null)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-white dark:bg-gray-950 pt-16 pb-20">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-600/15 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-accent-400/10 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>
        <div className="container-custom relative text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-100 dark:bg-primary-900/60 border border-primary-200 dark:border-primary-700/50 text-primary-700 dark:text-primary-400 text-xs font-semibold rounded-full uppercase tracking-widest mb-6">
              <Zap className="w-3 h-3" />
              Complete Learning Path
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
              DevOps Engineer{' '}
              <span className="bg-gradient-to-r from-primary-600 to-accent-500 dark:from-primary-400 dark:to-accent-400 bg-clip-text text-transparent">
                Roadmap 2026
              </span>
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              A structured, phase-by-phase journey from Linux basics to production-grade DevOps engineering.
              Every phase is backed by hands-on labs and real-world projects.
            </p>

            {/* Overview Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto mb-10">
              {overviewStats.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4 text-center"
                >
                  <div className="flex justify-center mb-2 text-primary-600 dark:text-primary-400">{s.icon}</div>
                  <div className="text-2xl font-extrabold text-gray-900 dark:text-white">{s.value}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</div>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl shadow-lg shadow-primary-600/25 transition-all hover:-translate-y-0.5"
              >
                Enroll & Start Learning
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/courses"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-500 hover:text-gray-900 dark:hover:text-white font-semibold rounded-xl transition-all"
              >
                <BookOpen className="w-4 h-4" />
                View All Courses
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Timeline ── */}
      <section className="bg-gray-50 dark:bg-gray-950 pb-24">
        <div className="container-custom">

          {/* Section label */}
          <div className="flex items-center gap-4 mb-10">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
            <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Learning Phases</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          </div>

          {/* Phase cards */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-primary-500 via-primary-300 to-gray-200 dark:from-primary-600 dark:via-primary-800 dark:to-gray-800 hidden md:block" />

            <div className="space-y-4">
              {phases.map((p, index) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="md:pl-16 relative"
                >
                  {/* Timeline dot */}
                  <div className={`hidden md:flex absolute left-0 top-6 w-12 h-12 rounded-full bg-gradient-to-br ${p.accent} items-center justify-center shadow-lg text-white font-extrabold text-xs z-10`}>
                    {String(p.id).padStart(2, '0')}
                  </div>

                  {/* Card */}
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden hover:border-gray-300 dark:hover:border-gray-700 transition-colors shadow-soft dark:shadow-none">

                    {/* Top gradient bar */}
                    <div className={`h-1 w-full bg-gradient-to-r ${p.accent}`} />

                    {/* Header (always visible) */}
                    <button
                      className="w-full text-left p-5 sm:p-6 focus:outline-none"
                      onClick={() => setOpenPhase(openPhase === p.id ? null : p.id)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1 min-w-0">
                          {/* Mobile number badge */}
                          <div className={`md:hidden flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${p.accent} flex items-center justify-center text-white font-bold text-sm`}>
                            {String(p.id).padStart(2, '0')}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{p.phase}</span>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.diffColor}`}>{p.difficulty}</span>
                              {p.badge && (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">
                                  🏆 {p.badge}
                                </span>
                              )}
                            </div>
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white leading-snug">{p.title}</h3>
                            <div className="flex items-center gap-1.5 mt-1.5 text-gray-500 dark:text-gray-400 text-sm">
                              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                              <span>{p.duration}</span>
                            </div>
                          </div>
                        </div>
                        <ChevronDown
                          className={`flex-shrink-0 w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform duration-300 mt-1 ${openPhase === p.id ? 'rotate-180' : ''}`}
                        />
                      </div>

                      {/* Tools preview (always visible) */}
                      <div className="flex flex-wrap gap-2 mt-4">
                        {p.tools.map((tool) => (
                          <span
                            key={tool}
                            className="px-2.5 py-1 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium rounded-lg"
                          >
                            {tool}
                          </span>
                        ))}
                      </div>
                    </button>

                    {/* Expandable details */}
                    <AnimatePresence>
                      {openPhase === p.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 sm:px-6 pb-6 border-t border-gray-100 dark:border-gray-800 pt-5">
                            <div className="grid sm:grid-cols-2 gap-6">

                              {/* Description */}
                              <div>
                                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-5">{p.description}</p>

                                {/* Outcome */}
                                <div className={`flex items-start gap-3 p-3.5 rounded-xl border ${p.lightAccent}`}>
                                  <CheckCircle className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-0.5">Phase Outcome</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">{p.outcome}</p>
                                  </div>
                                </div>
                              </div>

                              {/* Skills */}
                              <div>
                                <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Skills You'll Gain</p>
                                <ul className="space-y-2">
                                  {p.skills.map((skill) => (
                                    <li key={skill} className="flex items-start gap-2.5 text-sm text-gray-600 dark:text-gray-300">
                                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full bg-gradient-to-br ${p.accent} flex-shrink-0`} />
                                      {skill}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-900 dark:to-gray-900 border border-primary-500/30 dark:border-primary-800/50 rounded-2xl p-8 sm:p-10 text-center"
          >
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
              Ready to Start Your Journey?
            </h2>
            <p className="text-primary-100 dark:text-gray-400 mb-7 max-w-xl mx-auto">
              Our expert mentors guide you through every phase with live classes, hands-on labs, and placement support.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-primary-700 hover:bg-gray-50 font-bold rounded-xl transition-all hover:-translate-y-0.5 shadow-lg"
              >
                <Zap className="w-4 h-4" />
                Enroll Now
              </Link>
              <Link
                to="/batches"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border-2 border-white/40 text-white hover:bg-white/10 font-bold rounded-xl transition-all"
              >
                View Upcoming Batches
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

        </div>
      </section>

    </div>
  )
}

export default DevOpsRoadmap
